import { getFormattedDuration } from "../../../../utils/dateTime/library";
import Icon, { IconNames } from "../../../Foundation/Icons";
import { IconColours } from "../../../Foundation/Icons/interface";
import Badge from "../../Badge";
import { BadgeProps, BadgeTypes } from "../../Badge/interface";
import ImageWrapper from "../../ImageWrapper";
import { ImageTypes } from "../../ImageWrapper/interface";
import { TransformedCardData } from "../interface";
import classes from "./mediaImage.module.scss";

const DEFAULT_VIDEO_A11Y_TITLE = "Video content";
const DEFAULT_IMAGE_A11Y_TITLE = "Image content";

const createAccessibilityTitle = (
    type: string,
    title: string | undefined
): string => {
    if (type === "video" || type === "live") {
        return title ? `${title}, play video` : DEFAULT_VIDEO_A11Y_TITLE;
    }

    return title ? `${title}, view image` : DEFAULT_IMAGE_A11Y_TITLE;
};

type MediaImageProps = {
    item: TransformedCardData;
    type?: "video" | "image" | "live";
    aspectRatio?: number;
    showClosedCaptionsIcon?: boolean;
};

export const MediaImage = ({
    type = "image",
    aspectRatio,
    showClosedCaptionsIcon,
    item,
}: MediaImageProps) => {
    const { media, image, title } = item;
    if (!image) {
        return null;
    }

    const isVideoType = type === "video" || type === "live";
    const mediaIconType = isVideoType ? IconNames.Play : IconNames.ImageIcon;

    const videoInfoBadgeProps = {
        icon: showClosedCaptionsIcon ? IconNames.ClosedCaptions : undefined,
        iconRight: true,
    } as BadgeProps;

    if (isVideoType) {
        switch (type) {
            case "live":
                videoInfoBadgeProps.type = BadgeTypes.Live;
                videoInfoBadgeProps.text = "LIVE";
                break;
            case "video":
                videoInfoBadgeProps.type = BadgeTypes.NeutralDark;
                videoInfoBadgeProps.text = getFormattedDuration(
                    media?.duration ?? 0
                );
                break;
        }
    }

    return (
        <div className={classes.mediaImage}>
            <ImageWrapper
                imageType={ImageTypes.Image}
                aspectRatio={aspectRatio}
                {...image}
            />
            <div
                className={classes.mediaImageIcons}
                data-testid="media-image-icons"
            >
                <Icon
                    name={mediaIconType}
                    title={createAccessibilityTitle(type, title)}
                    colour={IconColours.black}
                    type="filled"
                    className={classes.mediaImageIconsMediaIcon}
                    size="24"
                    ariaHidden
                />
                {isVideoType && <Badge {...videoInfoBadgeProps} />}
            </div>
        </div>
    );
};
